/**
 * BLOG TAG PAYLOAD — the TS-L7 tranche carried out of vite.config.ts by the item-16 edit.
 *
 * WHAT IT IS FOR: the SSG bake reads blog posts straight out of Firestore and projects a
 * fixed field list into the page payload. That projection dropped every topic tag, so a
 * directly-loaded article shipped with no styles/regions/audience/ageGroups and its
 * call-to-action could not compute a target. This module is the one place that decides
 * which tags survive the bake and in what shape.
 *
 * ⚠️ WHY VALIDATION RATHER THAN A PASS-THROUGH, and this is the whole reason the module
 * exists instead of a spread: the payload is JSON-serialised into the page and read back
 * as `string[]`, while `HydratedBlogPost` types these fields as LITERAL UNIONS. A raw
 * pass-through type-checks only because `lib/blogSeed.ts` ends in an `as HydratedBlogPost`
 * cast (removed by this change) — without it, `string[]` is not assignable. So an
 * unvalidated carry-through would let an out-of-vocabulary tag reach `buildStyleUrl` and
 * mint a URL for a style that does not exist. Validating here is what makes removing that
 * cast safe.
 *
 * ⚠️ EMPTY MEANS ABSENT, DELIBERATELY. A post with no tags of a kind yields `undefined`
 * for that key, not `[]`, so the spread produces a byte-identical payload for an untagged
 * post. That is what keeps this change a no-op for the posts it does not concern, and it
 * is what makes the built-artefact diff readable.
 *
 * COVERS: the tag half of the blog payload only. It does NOT decide where the CTA points
 * — that is `lib/blogHubLinks.ts` (UI/UX's lane) — and it does not touch the listing
 * payload, which carries no tags and needs none (the index and category pages render the
 * CTA with no post attached).
 */
import {
    isMartialArtStyle,
    isBlogRegion,
    isBlogAudience,
    isBlogAgeGroup,
    type MartialArtStyle,
    type BlogPostRegion,
    type BlogAudience,
    type BlogAgeGroup,
} from '../src/constants/blogTags';

/** The tag block as it appears in the baked payload. Every key optional; absent when empty. */
export interface BlogTagPayload {
    styles?: MartialArtStyle[];
    regions?: BlogPostRegion[];
    audience?: BlogAudience[];
    ageGroups?: BlogAgeGroup[];
}

/**
 * Keep only values that are (a) strings and (b) in the vocabulary. Returns undefined for
 * an empty result so the caller's spread omits the key entirely.
 *
 * Non-array input yields undefined rather than throwing: this runs inside the prerender,
 * where a throw on one malformed document would fail the whole 4,000-page build. A dropped
 * tag degrades one call-to-action; a thrown error stops the site shipping.
 */
function keepValid<T extends string>(value: unknown, guard: (v: string) => boolean): T[] | undefined {
    if (!Array.isArray(value)) return undefined;
    const kept = value.filter((v): v is T => typeof v === 'string' && guard(v));
    return kept.length > 0 ? kept : undefined;
}

/**
 * Project the tag fields of a raw Firestore blog document into the baked payload.
 *
 * @param data a raw `blog_posts` document (untyped by construction — it is `doc.data()`)
 */
export function pickBlogTags(data: unknown): BlogTagPayload {
    const d = (data ?? {}) as Record<string, unknown>;
    const out: BlogTagPayload = {};

    const styles = keepValid<MartialArtStyle>(d.styles, isMartialArtStyle);
    if (styles) out.styles = styles;

    const regions = keepValid<BlogPostRegion>(d.regions, isBlogRegion);
    if (regions) out.regions = regions;

    const audience = keepValid<BlogAudience>(d.audience, isBlogAudience);
    if (audience) out.audience = audience;

    const ageGroups = keepValid<BlogAgeGroup>(d.ageGroups, isBlogAgeGroup);
    if (ageGroups) out.ageGroups = ageGroups;

    return out;
}

/** True when a projected payload carries no tags at all — i.e. the spread adds nothing. */
export function hasNoBlogTags(tags: BlogTagPayload): boolean {
    return tags.styles === undefined
        && tags.regions === undefined
        && tags.audience === undefined
        && tags.ageGroups === undefined;
}
